import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Slider,
  Statistic,
  Typography,
} from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompleteExperimentInput } from '../../schema/be-graphql-generated';
import useStyles from './styles.style';

const { Text } = Typography;

export type CompletionFields = Omit<
  CompleteExperimentInput,
  'subject' | 'experimentId'
>;

type CompletionFormValues = {
  patientReflections?: string | null;
  therapistReflections?: string | null;
  newThought?: string | null;
  newThoughtStrength?: number | null;
  oldThoughtStrength?: number | null;
  newLearnings?: string | null;
  meaningForOriginalBelief?: string | null;
  followUpExperimentsIdeas?: string | null;
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/**
 * E02 · Complete Experiment (portal, p6_complete) — opened from the Experiment Detail view's
 * "Finish experiment". Mirrors the mobile completion (m11): the DERIVED read-only box
 * (observationCount, durationDays = completedAt − activatedAt) + patient and therapist reflections,
 * the new thought / belief re-rating, new learnings, meaning for the original belief, follow-up
 * ideas. A second completion is a REVISION (latest wins; `completedAt` keeps the first instant —
 * registry best_call `completion-revision`), so the drawer also serves "Update completion".
 */
export const CompleteForm = ({
  open,
  subtitle,
  observationCount,
  durationDays,
  initial,
  isRevision,
  saving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  subtitle?: string;
  observationCount: number;
  durationDays?: number;
  initial?: CompletionFormValues;
  isRevision: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (fields: CompletionFields) => Promise<void> | void;
}) => {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const [form] = Form.useForm<CompletionFormValues>();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        patientReflections: str(initial?.patientReflections),
        therapistReflections: str(initial?.therapistReflections),
        newThought: str(initial?.newThought),
        newThoughtStrength: initial?.newThoughtStrength ?? 50,
        oldThoughtStrength: initial?.oldThoughtStrength ?? 50,
        newLearnings: str(initial?.newLearnings),
        meaningForOriginalBelief: str(initial?.meaningForOriginalBelief),
        followUpExperimentsIdeas: str(initial?.followUpExperimentsIdeas),
      });
    }
  }, [open, initial, form]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      title={t('pages.behaviorExperiment.completeTitle')}
      destroyOnClose
      data-testid="cc-behavior-experiment-complete-drawer"
      extra={
        <Button
          type="primary"
          loading={saving}
          onClick={() => form.submit()}
          data-testid="cc-behavior-experiment-complete-save"
        >
          {t(
            isRevision
              ? 'pages.behaviorExperiment.updateCompletion'
              : 'pages.behaviorExperiment.markFinished',
          )}
        </Button>
      }
    >
      {subtitle ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {subtitle}
        </Text>
      ) : null}

      <Card
        size="small"
        style={{ marginBottom: 24 }}
        data-testid="cc-behavior-experiment-complete-derived"
      >
        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
          <Statistic
            title={t('pages.behaviorExperiment.observationsRecorded')}
            value={observationCount}
          />
          <Statistic
            title={t('pages.behaviorExperiment.duration')}
            value={
              durationDays === undefined
                ? '—'
                : t('pages.behaviorExperiment.days', { n: durationDays })
            }
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('pages.behaviorExperiment.derivedNote')}
        </Text>
      </Card>

      <Form<CompletionFormValues>
        form={form}
        layout="vertical"
        onFinish={(values) =>
          onSubmit({
            patientReflections: str(values.patientReflections),
            therapistReflections: str(values.therapistReflections),
            newThought: str(values.newThought),
            newThoughtStrength: num(values.newThoughtStrength),
            oldThoughtStrength: num(values.oldThoughtStrength),
            newLearnings: str(values.newLearnings),
            meaningForOriginalBelief: str(values.meaningForOriginalBelief),
            followUpExperimentsIdeas: str(values.followUpExperimentsIdeas),
          })
        }
        data-testid="cc-behavior-experiment-complete-form"
      >
        <Form.Item
          name="patientReflections"
          label={t('shared.behaviorExperiment.patientReflections')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t(
              'shared.behaviorExperiment.patientReflectionsPlaceholder',
            )}
            data-testid="cc-behavior-experiment-complete-patient-reflections"
          />
        </Form.Item>
        <Form.Item
          name="therapistReflections"
          label={t('shared.behaviorExperiment.therapistReflections')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t(
              'shared.behaviorExperiment.therapistReflectionsPlaceholder',
            )}
            data-testid="cc-behavior-experiment-complete-therapist-reflections"
          />
        </Form.Item>
        <Form.Item
          name="newThought"
          label={t('shared.behaviorExperiment.newThought')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t('shared.behaviorExperiment.newThoughtPlaceholder')}
            data-testid="cc-behavior-experiment-complete-new-thought"
          />
        </Form.Item>
        <Form.Item
          name="newLearnings"
          label={t('shared.behaviorExperiment.newLearnings')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t('shared.behaviorExperiment.newLearningsPlaceholder')}
            data-testid="cc-behavior-experiment-complete-new-learnings"
          />
        </Form.Item>
        <Form.Item
          name="meaningForOriginalBelief"
          label={t('shared.behaviorExperiment.meaningForOriginalBelief')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t(
              'shared.behaviorExperiment.meaningForOriginalBeliefPlaceholder',
            )}
            data-testid="cc-behavior-experiment-complete-meaning"
          />
        </Form.Item>

        <Card
          size="small"
          title={t('pages.behaviorExperiment.beliefRerating')}
          style={{ marginBottom: 24 }}
        >
          <div className={styles['behavior-experiments--slider-row']}>
            <Form.Item
              name="oldThoughtStrength"
              label={t('shared.behaviorExperiment.oldThoughtStrength')}
              style={{ flex: 1, minWidth: 260 }}
            >
              <Slider
                min={1}
                max={100}
                data-testid="cc-behavior-experiment-complete-old-thought-strength"
              />
            </Form.Item>
            <Form.Item name="oldThoughtStrength">
              <InputNumber min={1} max={100} />
            </Form.Item>
          </div>
          <div className={styles['behavior-experiments--slider-row']}>
            <Form.Item
              name="newThoughtStrength"
              label={t('shared.behaviorExperiment.newThoughtStrength')}
              style={{ flex: 1, minWidth: 260 }}
            >
              <Slider
                min={1}
                max={100}
                data-testid="cc-behavior-experiment-complete-new-thought-strength"
              />
            </Form.Item>
            <Form.Item name="newThoughtStrength">
              <InputNumber min={1} max={100} />
            </Form.Item>
          </div>
        </Card>

        <Form.Item
          name="followUpExperimentsIdeas"
          label={t('shared.behaviorExperiment.followUpExperimentsIdeas')}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t(
              'shared.behaviorExperiment.followUpExperimentsIdeasPlaceholder',
            )}
            data-testid="cc-behavior-experiment-complete-follow-up"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
